import { create } from 'zustand'
import {
  getCachedBlob,
  setCachedBlob,
  clearExpired as clearExpiredDB,
  clearAll as clearAllDB
} from '@renderer/utils/assetCacheDB'

export interface CachedImage {
  blobUrl: string
  timestamp: number
}

interface ImageCacheState {
  cache: Map<string, CachedImage>
  defaultExpirationHours: number
  _isHydrated: boolean
  getCachedUrl: (url: string, expirationHours?: number) => Promise<string | undefined>
  setCachedUrl: (url: string, blobUrl: string, blob?: Blob) => Promise<void>
  clearCache: () => Promise<void>
  clearExpired: () => Promise<void>
  getCacheSize: () => number
  isExpired: (cached: CachedImage, expirationHours?: number) => boolean
  _hydrateFromIndexedDB: () => Promise<void>
}

export const useImageCacheStore = create<ImageCacheState>((set, get) => ({
  cache: new Map<string, CachedImage>(),
  defaultExpirationHours: 24,
  _isHydrated: false,

  getCachedUrl: async (url: string, expirationHours?: number): Promise<string | undefined> => {
    const state = get()
    const cached = state.cache.get(url)
    if (cached) {
      const expiration = expirationHours ?? state.defaultExpirationHours
      if (state.isExpired(cached, expiration)) {
        // Remove expired entry
        const newCache = new Map(state.cache)
        newCache.delete(url)
        // Revoke the blob URL to free memory
        URL.revokeObjectURL(cached.blobUrl)
        set({ cache: newCache })
        return undefined
      }
      return cached.blobUrl
    }

    // Check IndexedDB if not in memory
    if (state._isHydrated) {
      try {
        const blob = await getCachedBlob(url)
        if (blob) {
          const expiration = expirationHours ?? state.defaultExpirationHours
          // Check if blob is expired (we'll use a conservative timestamp)
          const timestamp = Date.now() - 1000 // Assume recent if from IndexedDB
          const expirationTime = timestamp + expiration * 60 * 60 * 1000
          if (Date.now() <= expirationTime) {
            const blobUrl = URL.createObjectURL(blob)
            const newCache = new Map(state.cache)
            newCache.set(url, {
              blobUrl,
              timestamp
            })
            set({ cache: newCache })
            return blobUrl
          }
        }
      } catch (error) {
        console.warn('Error loading from IndexedDB:', error)
      }
    }

    return undefined
  },

  setCachedUrl: async (url: string, blobUrl: string, blob?: Blob): Promise<void> => {
    const state = get()
    // If URL already exists, revoke old blob URL to prevent memory leak
    const existing = state.cache.get(url)
    if (existing) {
      URL.revokeObjectURL(existing.blobUrl)
    }

    // Store blob in IndexedDB if provided
    if (blob) {
      try {
        await setCachedBlob(url, blob)
      } catch (error) {
        console.warn('Failed to store blob in IndexedDB:', error)
        // Continue anyway - memory cache will still work
      }
    }

    const newCache = new Map(state.cache)
    newCache.set(url, {
      blobUrl,
      timestamp: Date.now()
    })
    set({ cache: newCache })
  },

  clearCache: async (): Promise<void> => {
    const state = get()
    // Revoke all blob URLs to free memory
    state.cache.forEach((cached) => {
      URL.revokeObjectURL(cached.blobUrl)
    })
    set({ cache: new Map() })
    // Clear IndexedDB
    try {
      await clearAllDB()
    } catch (error) {
      console.error('Error clearing IndexedDB:', error)
    }
  },

  clearExpired: async (): Promise<void> => {
    const state = get()
    const newCache = new Map(state.cache)
    let cleared = false

    newCache.forEach((cached, url) => {
      if (state.isExpired(cached)) {
        URL.revokeObjectURL(cached.blobUrl)
        newCache.delete(url)
        cleared = true
      }
    })

    if (cleared) {
      set({ cache: newCache })
    }

    // Clear expired from IndexedDB
    try {
      await clearExpiredDB(state.defaultExpirationHours)
    } catch (error) {
      console.error('Error clearing expired from IndexedDB:', error)
    }
  },

  getCacheSize: (): number => {
    return get().cache.size
  },

  isExpired: (cached: CachedImage, expirationHours?: number): boolean => {
    const state = get()
    const expiration = expirationHours ?? state.defaultExpirationHours
    const expirationTime = cached.timestamp + expiration * 60 * 60 * 1000
    return Date.now() > expirationTime
  },

  _hydrateFromIndexedDB: async (): Promise<void> => {
    const state = get()
    if (state._isHydrated) return

    // Load all cached URLs from IndexedDB and recreate blob URLs
    try {
      const { getAllCachedUrls } = await import('@renderer/utils/assetCacheDB')
      const urls = await getAllCachedUrls()
      const newCache = new Map(state.cache)

      for (const url of urls) {
        const blob = await getCachedBlob(url)
        if (blob) {
          const blobUrl = URL.createObjectURL(blob)
          // Use current timestamp for expiration calculation
          newCache.set(url, {
            blobUrl,
            timestamp: Date.now()
          })
        }
      }

      set({ cache: newCache, _isHydrated: true })
    } catch (error) {
      console.error('Error hydrating from IndexedDB:', error)
      set({ _isHydrated: true }) // Mark as hydrated even if failed
    }
  }
}))

// Hydrate from IndexedDB on store initialization
if (typeof window !== 'undefined') {
  // Hydrate after a short delay to ensure IndexedDB is ready
  setTimeout(() => {
    useImageCacheStore.getState()._hydrateFromIndexedDB()
  }, 100)
}
